import { ReactElement, ReactNode, useMemo } from 'react';
import {
    TasksAndAlertsHourlyActivityContext,
    TasksAndAlertsHourlyActivityContextData
} from './context';
import { useCompanyContext } from '@/companies/providers';
import { useTaskFilter } from '@/tasks/providers';
import { useFormatDate, useTimeSettings } from '@/common/hooks';
import {
    getEndOfDayInTimezone,
    getStartOfDayInTimezone
} from '@/common/util/format-date';
import { useFetchTasksAndAlertsHourlyActivity } from '../../queries';
import { buildAnalyticsQueryFilter } from '../../util/analytics-query-filter';
import { useReportsFilter } from '../reports-filter-provider';

type TasksAndAlertsHourlyActivityProviderProps = {
    readonly children?: ReactNode;
};

export function TasksAndAlertsHourlyActivityProvider({
    children
}: TasksAndAlertsHourlyActivityProviderProps): ReactElement {
    const { company } = useCompanyContext();
    const { teamIds, userIds, deviceIds } = useTaskFilter();
    const { range } = useReportsFilter();
    const { formatDateRange } = useFormatDate();
    const { timezone } = useTimeSettings();

    const query = useMemo(() => {
        return buildAnalyticsQueryFilter({
            companyId: company.id,
            teamIds,
            userIds,
            deviceIds,
            timezone,
            from: getStartOfDayInTimezone(range.from, timezone),
            to: getEndOfDayInTimezone(range.to, timezone)
        });
    }, [company.id, timezone, teamIds, userIds, deviceIds, range]);

    const { data, status } = useFetchTasksAndAlertsHourlyActivity(query);

    const dateString = useMemo(() => {
        if (data) {
            return formatDateRange(new Date(data.from), new Date(data.to));
        }
        return null;
    }, [data, formatDateRange]);

    const value: TasksAndAlertsHourlyActivityContextData = {
        status,
        tasks: data?.tasks ?? null,
        alerts: data?.alerts ?? null,
        dateString
    };

    return (
        <TasksAndAlertsHourlyActivityContext.Provider value={value}>
            {children}
        </TasksAndAlertsHourlyActivityContext.Provider>
    );
}
